import { useMediaPlayer } from "../lib/mediaPlayer";
import { Play, Pause, SkipForward, SkipBack, Repeat, Shuffle, Music2, Search, Video, Radio, Piano, ExternalLink, Volume2, VolumeX, Share2, SlidersHorizontal, RefreshCcw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { share, trackShareUrl } from "../lib/share";
import { api, imgUrl } from "../lib/api";

const TYPE_ICONS = { video: Video, audio: Music2, instrumental: Piano, podcast: Radio };
const DEFAULT_MEDIA_META = {
  video:        { label: "VIDEO",        color: "#E63946" },
  audio:        { label: "AUDIO",        color: "#FCA311" },
  instrumental: { label: "INSTRUMENTAL", color: "#F87171" },
  podcast:      { label: "PODCAST",      color: "#FDE047" },
};

// Split a multi-artist credit string like "Shao, Elaztic feat. Jynx" into individual names.
// Supports separators: comma, ampersand, plus, "x", "feat"/"feat.", "ft"/"ft.", "with", "vs"/"vs.".
const ARTIST_SPLIT_RE = /\s*(?:,|&|\+|\bx\b|\bfeat\.?\b|\bft\.?\b|\bwith\b|\bvs\.?\b)\s*/i;
export function splitArtists(raw) {
  if (!raw) return [];
  return raw.split(ARTIST_SPLIT_RE).map((s) => s.trim()).filter(Boolean);
}

function fmtTime(s) {
  if (!Number.isFinite(s) || s < 0) return "00:00";
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

export default function FamRadioPlayer() {
  const p = useMediaPlayer();
  const cfg = p.playerCfg || {};
  const mediaTypesCfg = cfg.media_types && cfg.media_types.length ? cfg.media_types : Object.entries(DEFAULT_MEDIA_META).map(([key, v]) => ({ key, ...v }));
  const metaByKey = useMemo(() => {
    const m = {};
    for (const mt of mediaTypesCfg) m[mt.key] = { label: mt.label, color: mt.color, Icon: TYPE_ICONS[mt.key] || Music2 };
    return m;
  }, [mediaTypesCfg]);
  const meta = metaByKey[p.mediaType] || { label: "AUDIO", color: "#FCA311", Icon: Music2 };
  const [pitchOpen, setPitchOpen] = useState(false);
  const [volumeOpen, setVolumeOpen] = useState(false);
  const [eqOpen, setEqOpen] = useState(false);
  const [rosterArtists, setRosterArtists] = useState([]);

  useEffect(() => {
    api.get("/artists").then((r) => setRosterArtists(r.data || [])).catch(() => {});
  }, []);

  const artistIdByName = useMemo(() => {
    const m = new Map();
    for (const a of rosterArtists) if (a.name) m.set(a.name.trim().toLowerCase(), a.id);
    return m;
  }, [rosterArtists]);

  const rosterByName = useMemo(() => {
    const m = new Map();
    for (const a of rosterArtists) if (a.name) m.set(a.name.trim().toLowerCase(), a);
    return m;
  }, [rosterArtists]);

  const archiveLinkFor = (rawName) => {
    if (!rawName) return "/artists";
    const primary = (splitArtists(rawName)[0] || rawName).trim().toLowerCase();
    const id = artistIdByName.get(primary);
    if (id) return `/artists?open=${id}`;
    return `/artists?q=${encodeURIComponent(primary || rawName)}`;
  };

  const shareTrack = (t) => {
    share({
      title: t.title || "Untitled",
      text: `${t.title || "Untitled"}${t.artist ? ` — ${t.artist}` : ""} · SyndaFam`,
      url: trackShareUrl(t.id),
    });
  };

  // Artists parsed from the currently playing track — this is what the chip cloud shows.
  const currentTrackArtists = useMemo(
    () => splitArtists(p.currentTrack?.artist || ""),
    [p.currentTrack?.artist]
  );

  // Publish full-player visibility to the MediaPlayer context so the MiniPlayer can hide while
  // the on-page FamRadioPlayer is in view. We observe the inner grid (fixed height ~820px) so
  // the visibility ratio is meaningful.
  const shellRef = useRef(null);
  const screenRef = useRef(null);
  const { setFullPlayerVisible, setVideoSlot } = p;

  // Register the on-page screen element as the "video slot" — while the
  // FamRadioPlayer is visible, the persistent YouTube iframe overlays this
  // node so the visitor sees the actual video (not just a static poster).
  useEffect(() => {
    setVideoSlot(screenRef.current);
    return () => setVideoSlot(null);
  }, [setVideoSlot]);

  useEffect(() => {
    const el = shellRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setFullPlayerVisible(true);
      return () => setFullPlayerVisible(false);
    }
    const obs = new IntersectionObserver(
      ([entry]) => setFullPlayerVisible(entry.isIntersecting && entry.intersectionRatio > 0.25),
      { threshold: [0, 0.25, 0.5, 0.85] }
    );
    obs.observe(el);
    return () => { obs.disconnect(); setFullPlayerVisible(false); };
  }, [setFullPlayerVisible]);

  return (
    <div className="w-full" data-testid="famradio-container">
      {/* TOP BAR — glass-morphism segmented control + subscribe */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-center mb-6">
        <div
          className="inline-flex flex-wrap items-center gap-1 p-1.5 rounded-full bg-white/[0.04] backdrop-blur-xl border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_10px_40px_-12px_rgba(0,0,0,0.6)] w-fit max-w-full"
          data-testid="famradio-type-pills"
        >
          {mediaTypesCfg.map((mt) => {
            const active = mt.key === p.mediaType;
            return (
              <button
                key={mt.key}
                onClick={() => p.setMediaType(mt.key)}
                className={`relative font-mono text-[10px] tracking-[0.3em] px-5 py-2.5 rounded-full transition-all duration-300 ease-out ${active ? "text-white" : "text-white/50 hover:text-white/90 hover:bg-white/[0.04]"}`}
                style={active ? {
                  background: `linear-gradient(180deg, ${mt.color}26, ${mt.color}0d)`,
                  boxShadow: `0 0 22px -6px ${mt.color}, inset 0 0 0 1px ${mt.color}66, inset 0 1px 0 rgba(255,255,255,0.18)`,
                } : undefined}
                data-testid={`famradio-type-${mt.key}`}
                aria-pressed={active}
              >
                {active && (
                  <span
                    aria-hidden
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{ background: `radial-gradient(70% 120% at 50% 50%, ${mt.color}33, transparent 70%)` }}
                  />
                )}
                <span className="relative" style={active ? { color: mt.color, textShadow: `0 0 12px ${mt.color}77` } : undefined}>
                  {mt.label}
                </span>
              </button>
            );
          })}
        </div>
        <a
          href={cfg.subscribe_url || "https://www.youtube.com/@syndafam"}
          target="_blank"
          rel="noopener noreferrer"
          className="justify-self-start lg:justify-self-end inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#C92A36] text-white font-mono text-xs tracking-[0.3em] px-6 py-3 rounded-full transition-all hover:shadow-[0_0_28px_-6px_rgba(230,57,70,0.9)] hover:-translate-y-px"
          data-testid="famradio-subscribe"
        >
          {cfg.subscribe_label || "SUBSCRIBE"}
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* MAIN GRID: LEFT screen+controls / RIGHT chips+tracklist — futuristic glass shell */}
      <div ref={shellRef} className="relative grid grid-cols-1 lg:grid-cols-12 gap-0 ai-border glass-card-strong rounded-3xl overflow-hidden lg:min-h-[820px]" style={{ perspective: "1600px" }} data-testid="famradio-player">
        {/* ambient drifting orbs behind the panel */}
        <div className="orb orb-red orb-live pointer-events-none" style={{ width: 340, height: 340, top: -80, left: -80 }} />
        <div className="orb orb-amber orb-live pointer-events-none" style={{ width: 280, height: 280, bottom: -60, right: -60, animationDelay: "-6s" }} />
        <div className="orb orb-cyan orb-live pointer-events-none" style={{ width: 220, height: 220, top: "50%", left: "40%", opacity: 0.14, animationDelay: "-3s" }} />
        {/* LEFT — video / artwork + controls */}
        <div className="relative lg:col-span-7 xl:col-span-8 p-5 sm:p-6 border-b lg:border-b-0 lg:border-r border-white/10">
          {/* Big screen with breathing color halo */}
          <div
            ref={screenRef}
            className="relative aspect-video overflow-hidden rounded-2xl bg-black border border-white/10 mb-5 flex items-center justify-center float-card"
            style={{ background: `radial-gradient(120% 90% at 50% 50%, ${meta.color}33 0%, #1a1a1a 60%, #0d0d0d 100%)`, boxShadow: `0 0 60px ${meta.color}22, inset 0 1px 0 rgba(255,255,255,0.05)` }}
            data-testid="famradio-screen"
          >
            {/* Behind the artwork: breathing halo of the current media-type colour */}
            <div
              className="absolute inset-6 rounded-full breathe pointer-events-none"
              style={{ background: `radial-gradient(circle, ${meta.color}55 0%, transparent 65%)`, opacity: 0.6 }}
              aria-hidden="true"
            />
            {p.currentTrack && p.currentTrack.youtube_id ? (
              // Video is streamed audio-only through the persistent (hidden)
              // iframe at the app root — this panel just shows the track's
              // YouTube poster as a static still so navigating between pages
              // never interrupts playback.
              <img
                key={`famradio-yt-thumb-${p.currentTrack.id}`}
                src={`https://i.ytimg.com/vi/${p.currentTrack.youtube_id}/maxresdefault.jpg`}
                onError={(e) => {
                  // Fall back to a lower-res still if maxresdefault is missing.
                  if (!e.currentTarget.dataset.fallback) {
                    e.currentTarget.dataset.fallback = "1";
                    e.currentTarget.src = `https://i.ytimg.com/vi/${p.currentTrack.youtube_id}/hqdefault.jpg`;
                  }
                }}
                alt={p.currentTrack.title || "Now playing"}
                className="w-full h-full object-cover"
                data-testid="famradio-yt-thumb"
              />
            ) : p.currentTrack && p.currentTrack.artwork_url ? (
              <img src={p.currentTrack.artwork_url} alt={p.currentTrack.title} className="w-full h-full object-cover" />
            ) : (
              <div className="flex flex-col items-center gap-3">
                <meta.Icon className="w-24 h-24" style={{ color: meta.color }} />
                <div className="font-display text-4xl tracking-widest" style={{ color: meta.color }}>
                  {meta.label}
                </div>
              </div>
            )}
            {/* meta corner */}
            <div className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.3em] bg-black/70 backdrop-blur px-2 py-1" style={{ color: meta.color }}>
              // {meta.label}
            </div>
            {p.currentTrack && (
              <div className="absolute bottom-3 right-3 font-mono text-[10px] tracking-widest bg-black/70 backdrop-blur px-2 py-1 text-white/80">
                #{String(p.filtered.findIndex((t) => t.id === p.currentTrack.id) + 1).padStart(2, "0")} / {String(p.filtered.length).padStart(2, "0")}
              </div>
            )}
          </div>

          {/* Now playing row */}
          <div className="flex items-center gap-4 border-l-4 pl-4 py-2 mb-5" style={{ borderColor: meta.color }} data-testid="famradio-now-playing">
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] tracking-[0.3em] text-white/40 mb-1">// NOW PLAYING</div>
              <div className="font-display text-2xl sm:text-3xl truncate">
                {p.currentTrack ? p.currentTrack.title || "Untitled" : "— nothing loaded —"}
              </div>
              {p.currentTrack?.artist && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5" data-testid="famradio-now-artists">
                  {currentTrackArtists.length > 0 ? currentTrackArtists.map((name, i) => (
                    <span key={`${name}-${i}`} className="inline-flex items-center gap-2">
                      {i > 0 && <span className="font-mono text-[10px] text-white/30">·</span>}
                      <Link
                        to={archiveLinkFor(name)}
                        className="font-mono text-xs tracking-widest text-[#FCA311] hover:underline"
                        data-testid={`famradio-now-artist-${name.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {name.toUpperCase()}
                      </Link>
                    </span>
                  )) : (
                    <Link to={archiveLinkFor(p.currentTrack.artist)} className="font-mono text-xs tracking-widest text-[#FCA311] hover:underline">
                      {p.currentTrack.artist.toUpperCase()}
                    </Link>
                  )}
                </div>
              )}
            </div>
            {p.currentTrack && (
              <button
                onClick={() => shareTrack(p.currentTrack)}
                className="flex-shrink-0 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] px-4 py-3 border-2 border-[#FCA311]/50 text-[#FCA311] hover:bg-[#FCA311] hover:text-black hover:border-[#FCA311] transition-all shadow-[0_0_20px_rgba(252,163,17,0.15)] hover:shadow-[0_0_28px_rgba(252,163,17,0.55)]"
                aria-label={`Share ${p.currentTrack.title || "current track"}`}
                title="Share this track"
                data-testid="famradio-share-current"
              >
                <Share2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">SHARE</span>
              </button>
            )}
          </div>

          {/* Big play + seek + prev/skip */}
          <div className="flex items-center gap-3 sm:gap-4 mb-6">
            <button
              onClick={p.prevTrack}
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl border-2 border-white/20 hover:border-[#FCA311] hover:text-[#FCA311] flex items-center justify-center transition-colors flex-shrink-0"
              aria-label="Previous track"
              title="Previous"
              data-testid="famradio-prev"
            >
              <SkipBack className="w-5 h-5" />
            </button>
            <button
              onClick={p.togglePlay}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[#E63946] hover:bg-[#C92A36] flex items-center justify-center shadow-[0_0_32px_rgba(230,57,70,0.55)] transition-colors flex-shrink-0"
              aria-label={p.playing ? "Pause" : "Play"}
              data-testid="famradio-play-toggle"
            >
              {p.playing ? <Pause className="w-8 h-8 text-white" /> : <Play className="w-8 h-8 text-white ml-1 fill-white" />}
            </button>
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <input
                type="range"
                min={0}
                max={Math.max(1, p.progress.duration)}
                step={0.1}
                value={p.progress.time || 0}
                onChange={(e) => p.seek(parseFloat(e.target.value))}
                className="w-full accent-[#E63946]"
                data-testid="famradio-seek"
              />
              <div className="flex justify-between font-mono text-[10px] tracking-widest text-white/50">
                <span data-testid="famradio-time-current">{fmtTime(p.progress.time)}</span>
                <span data-testid="famradio-time-total">{fmtTime(p.progress.duration)}</span>
              </div>
            </div>
            <button
              onClick={p.nextTrack}
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl border-2 border-white/20 hover:border-[#FCA311] hover:text-[#FCA311] flex items-center justify-center transition-colors flex-shrink-0"
              aria-label="Next"
              data-testid="famradio-next"
            >
              <SkipForward className="w-5 h-5" />
            </button>
          </div>

          {/* Secondary controls */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {[
              { k: "volume", label: p.muted || p.volume === 0 ? "MUTED" : `VOL ${Math.round(p.volume * 100)}`, Icon: (p.muted || p.volume === 0) ? VolumeX : Volume2, on: volumeOpen, onClick: () => setVolumeOpen((o) => !o) },
              { k: "eq", label: p.eqEnabled ? `EQ ${p.eqPreset.toUpperCase()}` : "EQ", Icon: SlidersHorizontal, on: eqOpen || p.eqEnabled, onClick: () => setEqOpen((o) => !o) },
              { k: "repeat", label: "REPEAT", Icon: Repeat, on: p.repeat, onClick: () => p.setRepeat(!p.repeat) },
              { k: "shuffle", label: "SHUFFLE", Icon: Shuffle, on: p.shuffle, onClick: () => p.setShuffle(!p.shuffle) },
              { k: "pitch", label: "PITCH", Icon: Piano, on: pitchOpen, onClick: () => setPitchOpen((o) => !o) },
            ].map((b) => (
              <button
                key={b.k}
                onClick={b.onClick}
                className={`flex flex-col items-center justify-center gap-1.5 py-4 rounded-xl border-2 transition-all ${b.on ? "bg-[#FCA311] border-[#FCA311] text-black shadow-[0_0_20px_rgba(252,163,17,0.45)]" : "border-white/15 text-white/70 hover:border-[#FCA311] hover:text-[#FCA311] hover:bg-white/[0.03]"}`}
                data-testid={`famradio-ctrl-${b.k}`}
              >
                <b.Icon className="w-5 h-5" />
                <span className="font-mono text-[10px] tracking-[0.3em]">{b.label}</span>
              </button>
            ))}
          </div>

          {volumeOpen && (
            <div className="flex items-center gap-3 border-2 border-[#FCA311]/40 px-4 py-3 mt-4" data-testid="famradio-volume-slider">
              <button
                onClick={() => p.setMuted(!p.muted)}
                className="text-white/80 hover:text-[#FCA311]"
                aria-label={p.muted ? "Unmute" : "Mute"}
                data-testid="famradio-volume-mute"
              >
                {p.muted || p.volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <span className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311]">VOLUME</span>
              <input
                type="range" min={0} max={100} step={1}
                value={p.muted ? 0 : Math.round(p.volume * 100)}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10) / 100;
                  p.setVolume(v);
                  if (v > 0 && p.muted) p.setMuted(false);
                }}
                className="flex-1 accent-[#FCA311]"
                aria-label="Volume"
                data-testid="famradio-volume-range"
              />
              <span className="font-mono text-[11px] text-white/80 w-10 text-right">{p.muted ? "0" : Math.round(p.volume * 100)}%</span>
            </div>
          )}

          {pitchOpen && (
            <div className="flex items-center gap-3 border-2 border-[#FCA311]/40 px-4 py-3 mt-4" data-testid="famradio-pitch-slider">
              <span className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311]">PITCH</span>
              <input
                type="range" min={-6} max={6} step={0.1} value={p.pitch}
                onChange={(e) => p.setPitch(parseFloat(e.target.value))}
                className="flex-1 accent-[#FCA311]"
              />
              <span className="font-mono text-xs text-white w-12 text-right tabular-nums">{p.pitch >= 0 ? "+" : ""}{p.pitch.toFixed(1)}</span>
              <button
                onClick={() => p.setPitch(0)}
                className="font-mono text-[10px] tracking-[0.3em] px-3 py-1.5 border-2 border-white/20 hover:border-[#E63946] hover:text-[#E63946]"
              >
                RESET
              </button>
            </div>
          )}

          {eqOpen && (
            <div className="border-2 border-[#FCA311]/40 rounded-xl px-4 py-4 mt-4 space-y-4" data-testid="famradio-eq-panel">
              {/* Header row: title + enable toggle + reset */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <SlidersHorizontal className="w-4 h-4 text-[#FCA311]" />
                  <span className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311]">EQUALIZER</span>
                  <button
                    onClick={() => p.setEqEnabled(!p.eqEnabled)}
                    className={`font-mono text-[9px] tracking-[0.3em] px-3 py-1.5 border-2 transition-colors ${p.eqEnabled ? "bg-[#E63946] border-[#E63946] text-white" : "border-white/20 text-white/60 hover:border-[#E63946] hover:text-[#E63946]"}`}
                    data-testid="famradio-eq-toggle"
                  >
                    {p.eqEnabled ? "ON" : "OFF"}
                  </button>
                </div>
                <button
                  onClick={() => { p.resetEq(); }}
                  className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] px-3 py-1.5 border-2 border-white/20 text-white/70 hover:border-[#E63946] hover:text-[#E63946] transition-colors"
                  data-testid="famradio-eq-reset"
                  title="Reset all bands to 0 dB"
                >
                  <RefreshCcw className="w-3 h-3" /> RESET
                </button>
              </div>

              {/* Preset chips */}
              <div className="flex flex-wrap gap-2" data-testid="famradio-eq-presets">
                {[
                  { k: "flat", label: "FLAT" },
                  { k: "bass-boost", label: "BASS BOOST" },
                  { k: "vocal", label: "VOCAL" },
                  { k: "rock", label: "ROCK" },
                  { k: "electronic", label: "ELECTRONIC" },
                  { k: "acoustic", label: "ACOUSTIC" },
                  { k: "hip_hop", label: "HIP HOP" },
                ].map((pr) => {
                  const active = p.eqPreset === pr.k && p.eqEnabled;
                  return (
                    <button
                      key={pr.k}
                      onClick={() => p.applyEqPreset(pr.k)}
                      className={`font-mono text-[9px] tracking-[0.3em] px-3 py-1.5 border-2 transition-colors ${active ? "bg-[#FCA311] border-[#FCA311] text-black" : "border-white/15 text-white/70 hover:border-[#FCA311] hover:text-[#FCA311]"}`}
                      data-testid={`famradio-eq-preset-${pr.k}`}
                    >
                      {pr.label}
                    </button>
                  );
                })}
              </div>

              {/* Band sliders — vertical */}
              <div className="grid grid-cols-5 gap-2 pt-2">
                {[
                  { i: 0, label: "BASS", freq: "60 Hz" },
                  { i: 1, label: "LO MID", freq: "250 Hz" },
                  { i: 2, label: "MID", freq: "1 kHz" },
                  { i: 3, label: "HI MID", freq: "4 kHz" },
                  { i: 4, label: "TREBLE", freq: "12 kHz" },
                ].map((band) => {
                  const val = p.eqBands[band.i] || 0;
                  return (
                    <div key={band.i} className="flex flex-col items-center gap-2" data-testid={`famradio-eq-band-${band.i}`}>
                      <div className="font-mono text-[9px] tracking-[0.2em] text-white/60">{band.label}</div>
                      <div className="relative h-32 flex items-center justify-center" style={{ writingMode: "vertical-lr" }}>
                        <input
                          type="range"
                          min={-12}
                          max={12}
                          step={0.5}
                          value={val}
                          onChange={(e) => p.updateEqBand(band.i, parseFloat(e.target.value))}
                          disabled={!p.eqEnabled}
                          className="h-32 accent-[#E63946] cursor-pointer disabled:opacity-40"
                          // Vertical orientation via CSS transform trick: rotate -90deg
                          style={{
                            writingMode: "vertical-lr",
                            direction: "rtl",
                            appearance: "slider-vertical",
                            width: 24,
                            height: 128,
                          }}
                          aria-label={`${band.label} ${band.freq}`}
                          data-testid={`famradio-eq-slider-${band.i}`}
                        />
                      </div>
                      <div className="font-mono text-[10px] tabular-nums text-white/80">{val > 0 ? "+" : ""}{val.toFixed(1)} dB</div>
                      <div className="font-mono text-[8px] tracking-widest text-white/30">{band.freq}</div>
                    </div>
                  );
                })}
              </div>

              {p.currentTrack?.youtube_id && (
                <div className="font-mono text-[9px] tracking-[0.2em] text-white/40 leading-relaxed pt-1" data-testid="famradio-eq-yt-hint">
                  // EQ affects audio-file tracks. YouTube playback is streamed through YouTube&rsquo;s own player and can&rsquo;t be re-processed by the browser.
                </div>
              )}
              {!p.currentTrack?.youtube_id && !p.eqAvailable && (
                <div className="font-mono text-[9px] tracking-[0.2em] text-white/40 leading-relaxed pt-1" data-testid="famradio-eq-cors-hint">
                  // EQ isn&rsquo;t wired for this track — the audio is hosted on a cross-origin CDN without CORS, so Web Audio can&rsquo;t process it. Presets still save for tracks that support it.
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — section switcher + chips + search + tracklist */}
        <div className="lg:col-span-5 xl:col-span-4 p-5 sm:p-6 flex flex-col min-h-0 lg:self-start lg:h-[820px]">
          {/* Chip cloud — artists on the currently playing track */}
          <div className="flex flex-wrap gap-3 mb-4 max-h-56 overflow-y-auto pr-1" data-testid="famradio-chips-artists">
            <div className="w-full font-mono text-[9px] tracking-[0.3em] text-white/40 mb-1" data-testid="famradio-chips-artists-label">
              // {p.currentTrack ? "ARTISTS ON THIS TRACK" : "SELECT AN ARTIST"}
            </div>
            <button
              onClick={() => p.setArtistFilter("")}
              className={`w-14 h-14 rounded-full flex items-center justify-center font-mono text-[9px] tracking-widest transition-all flex-shrink-0 ${!p.artistFilter ? "bg-white text-black shadow-[0_0_18px_#fff]" : "border-2 border-white/15 text-white/60 hover:border-white"}`}
              data-testid="famradio-chip-artist-all"
              title="Show all artists"
            >
              ALL
            </button>
            {(currentTrackArtists.length > 0
              ? currentTrackArtists.map((n) => ({ name: n }))
              : p.facets.artists.slice(0, 30)
            ).map((a) => {
              const primary = (splitArtists(a.name)[0] || a.name).trim().toLowerCase();
              const rosterArt = rosterByName.get(primary);
              const active = p.artistFilter.toLowerCase() === a.name.toLowerCase();
              const glow = active ? "#FCA311" : "#E63946";
              const initial = a.name.slice(0, 1).toUpperCase();
              return (
                <div
                  key={a.name}
                  className="relative group flex-shrink-0"
                  data-testid={`famradio-chip-artist-${a.name.replace(/\s+/g, '-').toLowerCase()}-wrap`}
                >
                  <button
                    onClick={() => p.setArtistFilter(active ? "" : a.name)}
                    className="block rounded-full overflow-hidden transition-transform duration-200 hover:scale-110"
                    style={{
                      width: 56,
                      height: 56,
                      border: `2px solid ${glow}`,
                      boxShadow: `0 0 12px ${glow}, 0 0 24px ${glow}55`,
                    }}
                    title={`Filter tracks by ${a.name}`}
                    data-testid={`famradio-chip-artist-${a.name.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {rosterArt?.photo_path ? (
                      <img src={imgUrl(rosterArt.photo_path)} alt={a.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-black font-display text-xl text-white/70">
                        {initial}
                      </div>
                    )}
                  </button>
                  {/* Open-in-archive overlay */}
                  <Link
                    to={archiveLinkFor(a.name)}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-black border border-white/40 flex items-center justify-center text-white/80 opacity-0 group-hover:opacity-100 hover:text-[#FCA311] hover:border-[#FCA311] transition-opacity"
                    title={`Open ${a.name} in archive`}
                    data-testid={`famradio-chip-artist-open-${a.name.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <ExternalLink className="w-2.5 h-2.5" />
                  </Link>
                  {/* Name label */}
                  <div
                    className={`text-center mt-1 font-mono text-[8px] tracking-widest truncate w-14 ${active ? "text-[#FCA311]" : "text-white/60"}`}
                  >
                    {a.name.toUpperCase()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              value={p.search}
              onChange={(e) => p.setSearch(e.target.value)}
              placeholder={cfg.search_placeholder || "Search songs\u2026"}
              className="w-full bg-black border-2 border-white/15 focus:border-[#E63946] outline-none pl-10 pr-3 py-3 font-mono text-sm placeholder:text-white/30"
              data-testid="famradio-search"
            />
          </div>

          {/* Tracklist — glassy scrollable list with row glow on hover/active */}
          <div className="flex-1 min-h-0 rounded-2xl border border-white/10 bg-black/30 overflow-y-auto" data-testid="famradio-tracklist">
            {p.artistFilter && (
              <Link
                to={archiveLinkFor(p.artistFilter)}
                className="flex items-center justify-between px-4 py-3 bg-[#FCA311]/10 border-b border-[#FCA311]/40 text-[#FCA311] hover:bg-[#FCA311]/20 transition-colors"
                data-testid="famradio-open-artist-archive"
              >
                <span className="font-mono text-[10px] tracking-[0.3em]">OPEN {p.artistFilter.toUpperCase()} IN ARCHIVE</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            )}
            {p.filtered.length === 0 && (
              <div className="font-mono text-xs text-white/40 text-center py-8" data-testid="famradio-empty">
                {cfg.empty_message || "No songs found in this playlist yet"}
              </div>
            )}
            {p.filtered.map((t, i) => {
              const active = t.id === p.currentTrack?.id;
              return (
                <div
                  key={t.id}
                  className={`group grid grid-cols-[36px_1fr_auto] items-center gap-3 px-4 py-3 border-b border-white/5 transition-all duration-300 ${active ? "bg-[#E63946]/15 shadow-[inset_2px_0_0_#E63946,inset_0_0_40px_rgba(230,57,70,0.15)]" : "hover:bg-white/[0.04] hover:shadow-[inset_2px_0_0_rgba(252,163,17,0.6),inset_0_0_30px_rgba(252,163,17,0.08)]"}`}
                  data-testid={`famradio-track-${t.id}`}
                >
                  <button
                    onClick={() => p.playTrack(t.id)}
                    className={`font-mono text-[10px] tracking-widest w-8 h-8 flex items-center justify-center ${active ? "text-[#E63946]" : "text-white/40"} hover:text-white`}
                    aria-label={active && p.playing ? "Pause" : "Play"}
                    data-testid={`famradio-track-play-${t.id}`}
                  >
                    {active && p.playing ? <Pause className="w-4 h-4" /> : <span>{String(i + 1).padStart(2, "0")}</span>}
                  </button>
                  <div className="min-w-0">
                    <button
                      onClick={() => p.playTrack(t.id)}
                      className={`block w-full text-left font-display text-base truncate ${active ? "text-white" : "text-white/90 hover:text-white"}`}
                      data-testid={`famradio-track-title-${t.id}`}
                    >
                      {t.title}
                    </button>
                    {t.artist && (
                      <div className="font-mono text-[10px] tracking-widest text-white/50 truncate">
                        {splitArtists(t.artist).length > 0 ? splitArtists(t.artist).map((name, idx) => (
                          <span key={`${t.id}-${name}-${idx}`}>
                            {idx > 0 && <span className="text-white/25 mx-1">·</span>}
                            <Link
                              to={archiveLinkFor(name)}
                              className="hover:text-[#FCA311] transition-colors"
                              data-testid={`famradio-track-artist-link-${t.id}-${name.replace(/\s+/g, '-').toLowerCase()}`}
                              title={`Open ${name} in archive`}
                            >
                              {name}
                            </Link>
                          </span>
                        )) : (
                          <Link
                            to={archiveLinkFor(t.artist)}
                            className="hover:text-[#FCA311] transition-colors"
                            data-testid={`famradio-track-artist-link-${t.id}`}
                          >
                            {t.artist}
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="font-mono text-[10px] tracking-widest text-white/30 uppercase flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); shareTrack(t); }}
                      className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-[#FCA311] transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                      aria-label={`Copy share link for ${t.title}`}
                      title="Copy share link"
                      data-testid={`famradio-track-share-${t.id}`}
                    >
                      <Share2 className="w-3 h-3" />
                    </button>
                    <span>{t.media_type || "audio"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
